var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Derivaciones del dashboard de reportes a partir de la respuesta de
 * `quoteAll`. Todo se calcula sobre `catalogStatusQuote.code`:
 *
 *   A = cotizada (solo cotizó)   E = emitida (vendida)   C = cancelada
 *
 * "Cotizaciones" incluye las emitidas porque toda venta empezó como cotización.
 */
export var QUOTE_STATUS = {
    quoted: "A",
    issued: "E",
    cancelled: "C",
};
var PRODUCT_COLORS = [
    "#3A56A1",
    "#6c5ce7",
    "#0d9488",
    "#d97706",
    "#dc2626",
];
var MAX_PRODUCTS = 5;
var UNKNOWN_PRODUCT = "Sin producto";
var statusOf = function (row) { var _a; return (_a = row.catalogStatusQuote) === null || _a === void 0 ? void 0 : _a.code; };
var isIssued = function (row) { return statusOf(row) === QUOTE_STATUS.issued; };
var isQuoted = function (row) {
    return statusOf(row) === QUOTE_STATUS.quoted || isIssued(row);
};
var isCancelled = function (row) { return statusOf(row) === QUOTE_STATUS.cancelled; };
var productOf = function (row) { var _a, _b; return ((_b = (_a = row.productId) === null || _a === void 0 ? void 0 : _a.description) === null || _b === void 0 ? void 0 : _b.trim()) || UNKNOWN_PRODUCT; };
/** `amount` llega como string y puede traer coma de miles: "205,000.00". */
export var parseAmount = function (amount) {
    var parsed = parseFloat((amount || "0").replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
};
/** 1_200_000 -> "RD$1.2M" · 95_000 -> "RD$95K" */
export var formatPremium = function (value, prefix) {
    if (prefix === void 0) { prefix = "RD$"; }
    if (value >= 1000000) {
        return "".concat(prefix).concat((value / 1000000).toFixed(1), "M");
    }
    if (value >= 1000) {
        return "".concat(prefix).concat((value / 1000).toFixed(0), "K");
    }
    return "".concat(prefix).concat(value.toFixed(0));
};
export var countIssued = function (data) { return data.filter(isIssued).length; };
export var countQuoted = function (data) { return data.filter(isQuoted).length; };
export var countCancelled = function (data) {
    return data.filter(isCancelled).length;
};
export var sumPremium = function (data) {
    return data
        .filter(isIssued)
        .reduce(function (total, row) { var _a; return total + parseAmount((_a = row.lastPrice) === null || _a === void 0 ? void 0 : _a.amount); }, 0);
};
var dayOf = function (value) { return value === null || value === void 0 ? void 0 : value.substring(0, 10); };
/** Serie diaria de conteos para las sparklines de los KPIs. */
var countByDay = function (data, matches, dateField) {
    var groups = {};
    data.filter(matches).forEach(function (row) {
        var day = dayOf(row[dateField]);
        if (!day) {
            return;
        }
        groups[day] = (groups[day] || 0) + 1;
    });
    return Object.keys(groups)
        .sort()
        .map(function (day) { return groups[day]; });
};
/** Serie diaria de prima emitida, para la sparkline de "Prima recaudada". */
var premiumByDay = function (data) {
    var groups = {};
    data.filter(isIssued).forEach(function (row) {
        var _a;
        var day = dayOf(row.creationDate);
        if (!day) {
            return;
        }
        groups[day] = (groups[day] || 0) + parseAmount((_a = row.lastPrice) === null || _a === void 0 ? void 0 : _a.amount);
    });
    return Object.keys(groups)
        .sort()
        .map(function (day) { return Math.round(groups[day]); });
};
/** El componente recibe el historial de cada KPI como string[]. */
var toCardValues = function (series) { return series.map(String); };
export var salesSparkline = function (data) {
    return toCardValues(countByDay(data, isIssued, "creationDate"));
};
export var quotesSparkline = function (data) {
    return toCardValues(countByDay(data, function (row) { return statusOf(row) === QUOTE_STATUS.quoted; }, "creationDate"));
};
/** Las cancelaciones se agrupan por `cancellationDate`, no por creación. */
export var cancellationsSparkline = function (data) {
    return toCardValues(countByDay(data, isCancelled, "cancellationDate"));
};
export var premiumSparkline = function (data) {
    return toCardValues(premiumByDay(data));
};
/** Etiqueta corta para el eje X: "27 Jun". */
var shortDayLabel = function (day) {
    var date = new Date("".concat(day, "T00:00:00"));
    return "".concat(date.getDate(), " ").concat(date.toLocaleString("es", { month: "short" }));
};
/**
 * Tendencia en el tiempo: ventas por fecha de creación y cancelaciones por
 * fecha de cancelación, alineadas sobre el mismo eje de días.
 */
export var buildTrend = function (data) {
    var sales = {};
    var cancellations = {};
    data.forEach(function (row) {
        if (isIssued(row)) {
            var day = dayOf(row.creationDate);
            if (day) {
                sales[day] = (sales[day] || 0) + 1;
            }
        }
        if (isCancelled(row)) {
            var day = dayOf(row.cancellationDate);
            if (day) {
                cancellations[day] = (cancellations[day] || 0) + 1;
            }
        }
    });
    var days = Array.from(new Set(__spreadArray(__spreadArray([], Object.keys(sales), true), Object.keys(cancellations), true))).sort();
    return {
        categories: days.map(shortDayLabel),
        sales: days.map(function (day) { return sales[day] || 0; }),
        cancellations: days.map(function (day) { return cancellations[day] || 0; }),
    };
};
/**
 * Alimenta las tres tarjetas por producto (barras, dona y embudo) con una
 * sola pasada: ventas = emitidas, prima = suma de emitidas, cotizaciones =
 * cotizadas + emitidas. Se devuelven los productos con más ventas.
 */
export var buildProductsData = function (data) {
    var groups = {};
    data.forEach(function (row) {
        var _a;
        var product = productOf(row);
        if (!groups[product]) {
            groups[product] = { sales: 0, premium: 0, quotes: 0 };
        }
        if (isIssued(row)) {
            groups[product].sales += 1;
            groups[product].premium += parseAmount((_a = row.lastPrice) === null || _a === void 0 ? void 0 : _a.amount);
        }
        if (isQuoted(row)) {
            groups[product].quotes += 1;
        }
    });
    return Object.keys(groups)
        .map(function (label) { return (__assign({ label: label }, groups[label])); })
        .filter(function (product) { return product.quotes > 0; })
        .sort(function (a, b) { return b.sales - a.sales; })
        .slice(0, MAX_PRODUCTS)
        .map(function (product, index) { return (__assign(__assign({}, product), { color: PRODUCT_COLORS[index % PRODUCT_COLORS.length] })); });
};
